import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { startAuthentication, startRegistration } from '@simplewebauthn/browser'
import Header from '@/components/Header'
import Footer from '@/components/Footer'

type Status = 'idle' | 'logging-in' | 'registering' | 'error'

function AdminLoginPage() {
    const navigate = useNavigate()
    const [email, setEmail] = useState('')
    const [status, setStatus] = useState<Status>('idle')
    const [errorMsg, setErrorMsg] = useState('')

    const busy = status === 'logging-in' || status === 'registering'

    function setError(msg: string) {
        setErrorMsg(msg)
        setStatus('error')
    }

    const handleLogin = async (e: React.FormEvent) => {
        e.preventDefault()
        if (!email) return
        setStatus('logging-in')
        setErrorMsg('')

        try {
            const beginRes = await fetch('/api/admin/auth/begin', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email }),
            })

            if (!beginRes.ok) {
                const data = await beginRes.json()
                setError(data.error || 'Failed to start authentication')
                return
            }

            const options = await beginRes.json()
            const credential = await startAuthentication(options)

            const completeRes = await fetch('/api/admin/auth/complete', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(credential),
            })

            if (!completeRes.ok) {
                const data = await completeRes.json()
                setError(data.error || 'Authentication failed')
                return
            }

            navigate('/admin')
        } catch (err: any) {
            if (err?.name === 'NotAllowedError') {
                setError('Authentication was cancelled or timed out.')
            } else {
                setError('An unexpected error occurred. Please try again.')
            }
            console.error('[AdminLogin] Error:', err)
        }
    }

    const handleRegister = async () => {
        if (!email) {
            setError('Please enter your email address first.')
            return
        }
        setStatus('registering')
        setErrorMsg('')

        try {
            const beginRes = await fetch('/api/admin/register/begin', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email }),
            })

            if (!beginRes.ok) {
                const data = await beginRes.json()
                if (beginRes.status === 403) {
                    setError('This email is not authorized. You must be a member of the Shipping Admins group.')
                } else {
                    setError(data.error || 'Failed to start registration')
                }
                return
            }

            const options = await beginRes.json()
            const credential = await startRegistration(options)

            const completeRes = await fetch(
                `/api/admin/register/complete?email=${encodeURIComponent(email)}`,
                {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(credential),
                }
            )

            if (!completeRes.ok) {
                const data = await completeRes.json()
                setError(data.error || 'Registration failed')
                return
            }

            // Key registered — immediately attempt login with it
            setStatus('logging-in')
            const beginAuth = await fetch('/api/admin/auth/begin', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email }),
            })

            if (!beginAuth.ok) {
                setError('Key registered. Please use the login button to sign in.')
                return
            }

            const authOptions = await beginAuth.json()
            const authCredential = await startAuthentication(authOptions)

            const completeAuth = await fetch('/api/admin/auth/complete', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(authCredential),
            })

            if (!completeAuth.ok) {
                setError('Key registered. Please use the login button to sign in.')
                return
            }

            navigate('/admin')
        } catch (err: any) {
            if (err?.name === 'NotAllowedError') {
                setError('Operation was cancelled or timed out.')
            } else if (err?.name === 'InvalidStateError') {
                setError('This security key is already registered.')
            } else {
                setError('An unexpected error occurred. Please try again.')
            }
            console.error('[AdminRegister] Error:', err)
        }
    }

    return (
        <div className="min-h-screen bg-gray-50 flex flex-col">
            <Header variant="default" />

            <main className="flex-grow flex items-center justify-center p-6 py-12">
                <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-8 border border-gray-100">

                    <div className="text-center mb-8">
                        <div className="inline-block p-3 bg-yubico-green-50 rounded-2xl mb-4">
                            <i className="iconoir-lock text-yubico-green-400 text-3xl"></i>
                        </div>
                        <h2 className="text-3xl font-bold text-gray-900">Admin Portal</h2>
                        <p className="text-gray-500 mt-2">Enter your email and authenticate with your YubiKey</p>
                    </div>

                    <form onSubmit={handleLogin} className="space-y-5">
                        <div className="space-y-1">
                            <label htmlFor="email" className="block text-sm font-bold text-gray-700">
                                Email Address
                            </label>
                            <input
                                type="email"
                                id="email"
                                value={email}
                                onChange={e => setEmail(e.target.value)}
                                className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-yubico-green-400 outline-none transition-all bg-gray-50 focus:bg-white"
                                placeholder="admin@yubico.com"
                                required
                                disabled={busy}
                            />
                        </div>

                        {status === 'error' && (
                            <div className="p-4 bg-red-50 text-red-700 rounded-xl text-sm border border-red-100">
                                {errorMsg}
                            </div>
                        )}

                        {/* Login button */}
                        <button
                            type="submit"
                            disabled={busy}
                            className={`w-full font-bold py-4 px-4 rounded-xl transition-all flex items-center justify-center gap-2 transform hover:scale-[1.02] active:scale-[0.98] ${
                                busy
                                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                    : 'bg-yubico-green-400 hover:bg-yubico-green-500 text-white shadow-lg shadow-yubico-green-400/20'
                            }`}
                        >
                            {status === 'logging-in' ? (
                                <>
                                    <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                    <span>Touch your security key...</span>
                                </>
                            ) : (
                                <>
                                    <i className="iconoir-key-alt text-lg"></i>
                                    <span>Login with Security Key</span>
                                </>
                            )}
                        </button>

                        {/* Divider */}
                        <div className="flex items-center gap-3">
                            <div className="flex-1 h-px bg-gray-200" />
                            <span className="text-xs text-gray-400 font-medium">or</span>
                            <div className="flex-1 h-px bg-gray-200" />
                        </div>

                        {/* Register button */}
                        <button
                            type="button"
                            onClick={handleRegister}
                            disabled={busy}
                            className={`w-full font-bold py-4 px-4 rounded-xl border-2 transition-all flex items-center justify-center gap-2 transform hover:scale-[1.02] active:scale-[0.98] ${
                                busy
                                    ? 'border-gray-200 text-gray-400 cursor-not-allowed'
                                    : 'border-yubico-green-400 text-yubico-green-400 hover:bg-yubico-green-50'
                            }`}
                        >
                            {status === 'registering' ? (
                                <>
                                    <span className="w-5 h-5 border-2 border-yubico-green-400/30 border-t-yubico-green-400 rounded-full animate-spin" />
                                    <span>Touch your security key...</span>
                                </>
                            ) : (
                                <>
                                    <i className="iconoir-key-alt text-lg"></i>
                                    <span>Register New Key</span>
                                </>
                            )}
                        </button>
                    </form>

                    <p className="mt-5 text-center text-xs text-gray-400">
                        Registration requires membership in the Shipping Admins group
                    </p>
                </div>
            </main>

            <Footer variant="default" />
        </div>
    )
}

export default AdminLoginPage
